/* eslint-disable @typescript-eslint/no-explicit-any */

import { api, ApiResponse } from '@/api/api';
import { JOB_CANDIDATE_URLS, MEETING_URLS } from '@/api/url';
import { Meeting, MeetingSummary, Transcript } from '@/types/Meeting';
import { MeetingTranscriptBookmark } from '@/types/enums';
import { isAxiosError } from 'axios';

// Constants
const ERROR_MESSAGES = {
  INVALID_PAYLOAD: 'Invalid meeting payload provided',
  MEETING_NOT_FOUND: 'Meeting not found',
  TRANSCRIPT_NOT_FOUND: 'Transcript not found',
  UNKNOWN_ERROR: 'An unknown error occurred'
};

// Types
export interface CreateMeetingPayload {
  title: string;
  meeting_url: string;
  is_live: boolean;
  start_time?: number;
  end_time?: number;
}

export interface CreateMockMeetingPayload {
  quality_level: 'very_strong' | 'strong' | 'medium' | 'weak' | 'very_weak';
}

export interface GetMeetingDashboardResponse {
  recent_meetings: MeetingSummary[];
  upcoming_meetings: MeetingSummary[];
}

export interface GetTranscriptsResponse {
  transcripts: Array<Transcript>;
}

export interface BookmarkTranscriptPayload {
  transcript_id: number;
  bookmark: MeetingTranscriptBookmark;
}

export interface UpdateMeetingNotePayload {
  note: string;
}

export interface GetRecentMeetingsParams {
  page: number;
  limit?: number;
}

export interface UpdateMeetingPayload {
  title: string;
  meeting_url: string;
  start_time: number;
  end_time: number;
}

export interface UpdateMeetingURLPayload {
  meeting_url: string;
}

export interface ImportMockTranscriptPayload {
  transcripts: Transcript[];
}

/**
 * Creates a new meeting for a candidate
 * @param jobId - The ID of the job
 * @param candidateId - The ID of the candidate
 * @param payload - The meeting creation payload
 * @returns Promise<ApiResponse<Meeting>> - The API response containing the created meeting
 * @throws Error if the payload is invalid or if an unknown error occurs
 */
export const apiCreateMeeting = async ({
  jobId,
  candidateId,
  payload
}: {
  jobId: string;
  candidateId: string;
  payload: CreateMeetingPayload;
}): Promise<ApiResponse<Meeting>> => {
  try {
    // Validate payload
    if (!payload.title || !payload.meeting_url) {
      throw new Error(ERROR_MESSAGES.INVALID_PAYLOAD);
    }

    const response = await api.post<ApiResponse<Meeting>>(
      JOB_CANDIDATE_URLS.CREATE_MEETING(jobId, candidateId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Creates a mock meeting for a candidate
 * @param jobId - The ID of the job
 * @param candidateId - The ID of the candidate
 * @param payload - The mock meeting creation payload
 * @returns Promise<ApiResponse<Meeting>> - The API response containing the created meeting
 */
export const apiCreateMockMeeting = async ({
  jobId,
  candidateId,
  payload
}: {
  jobId: string;
  candidateId: string;
  payload: CreateMockMeetingPayload;
}): Promise<ApiResponse<Meeting>> => {
  try {
    const response = await api.post<ApiResponse<Meeting>>(
      JOB_CANDIDATE_URLS.CREATE_MOCK_MEETING(jobId, candidateId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Retrieves the meeting dashboard with recent and upcoming meetings
 * @returns Promise<ApiResponse<GetMeetingDashboardResponse>> - The API response containing the dashboard data
 * @throws Error if an unknown error occurs
 */
export const apiGetMeetingDashboard = async (): Promise<
  ApiResponse<GetMeetingDashboardResponse>
> => {
  try {
    const response = await api.get<ApiResponse<GetMeetingDashboardResponse>>(
      MEETING_URLS.GET_DASHBOARD
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Retrieves a specific meeting by ID
 * @param meetingId - The ID of the meeting to retrieve
 * @param updateTranscript - Whether to update the transcript
 * @returns Promise<ApiResponse<Meeting>> - The API response containing the meeting data
 * @throws Error if the meeting is not found or if an unknown error occurs
 */
export const apiGetMeeting = async ({
  meetingId,
  updateTranscript = false
}: {
  meetingId: string;
  updateTranscript?: boolean;
}): Promise<ApiResponse<Meeting>> => {
  try {
    const response = await api.get<ApiResponse<Meeting>>(MEETING_URLS.GET(meetingId), {
      params: {
        update_transcript: updateTranscript
      }
    });
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error(ERROR_MESSAGES.MEETING_NOT_FOUND);
      }
      return error.response?.data;
    }

    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Retrieves transcripts for a specific meeting
 * @param meetingId - The ID of the meeting
 * @returns Promise<ApiResponse<GetTempTranscriptsResponse>> - The API response containing the transcripts
 * @throws Error if the transcripts are not found or if an unknown error occurs
 */
export const apiGetTranscripts = async (
  meetingId: string
): Promise<ApiResponse<GetTranscriptsResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetTranscriptsResponse>>(
      MEETING_URLS.GET_TRANSCRIPT(meetingId)
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error(ERROR_MESSAGES.TRANSCRIPT_NOT_FOUND);
      }
      return error.response?.data;
    }

    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Bookmarks a transcript
 * @param meetingId - The ID of the meeting
 * @param payload - The bookmark payload
 * @returns Promise<ApiResponse<void>> - The API response
 */
export const apiBookmarkTranscript = async (
  meetingId: string,
  payload: BookmarkTranscriptPayload
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(
      MEETING_URLS.BOOKMARK_TRANSCRIPT(meetingId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Updates a meeting note
 * @param meetingId - The ID of the meeting
 * @param payload - The note payload
 * @returns Promise<ApiResponse<void>> - The API response
 */
export const apiUpdateMeetingNote = async (
  meetingId: string,
  payload: UpdateMeetingNotePayload
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(MEETING_URLS.UPDATE_NOTE(meetingId), payload);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Fetches recent meetings
 * @param params - The parameters for pagination
 * @returns Promise<ApiResponse<MeetingSummary[]>> - The API response containing recent meetings
 */
export const apiGetRecentMeetings = async (
  params: GetRecentMeetingsParams
): Promise<ApiResponse<MeetingSummary[]>> => {
  try {
    const response = await api.get<ApiResponse<MeetingSummary[]>>(
      MEETING_URLS.GET_RECENT_MEETINGS,
      {
        params
      }
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Fetches upcoming meetings
 * @param params - The parameters for pagination
 * @returns Promise<ApiResponse<MeetingSummary[]>> - The API response containing upcoming meetings
 */
export const apiGetUpcomingMeetings = async (): Promise<ApiResponse<MeetingSummary[]>> => {
  try {
    const response = await api.get<ApiResponse<MeetingSummary[]>>(
      MEETING_URLS.GET_UPCOMING_MEETINGS
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Deletes a meeting
 * @param meetingId - The ID of the meeting to delete
 * @returns Promise<ApiResponse<void>> - The API response
 */
export const apiDeleteMeeting = async (meetingId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete<ApiResponse<void>>(MEETING_URLS.DELETE_MEETING(meetingId));
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Updates a meeting
 * @param meetingId - The ID of the meeting to update
 * @param payload - The update payload
 * @returns Promise<ApiResponse<void>> - The API response
 */
export const apiUpdateMeeting = async (
  meetingId: string,
  payload: UpdateMeetingPayload
): Promise<ApiResponse<Meeting>> => {
  try {
    const response = await api.put<ApiResponse<Meeting>>(
      MEETING_URLS.UPDATE_MEETING(meetingId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Updates a meeting URL
 * @param meetingId - The ID of the meeting to update
 * @param payload - The update URL payload
 * @returns Promise<ApiResponse<Meeting>> - The API response
 */
export const apiUpdateMeetingURL = async (
  meetingId: string,
  payload: UpdateMeetingURLPayload
): Promise<ApiResponse<Meeting>> => {
  try {
    const response = await api.put<ApiResponse<Meeting>>(
      MEETING_URLS.UPDATE_MEETING_URL(meetingId),
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * Imports a mock transcript
 * @param meetingId - The ID of the meeting
 * @param payload - The import payload
 * @returns Promise<ApiResponse<Meeting>> - The API response
 */
export const apiImportMockTranscript = async (
  meetingId: string,
  payload: ImportMockTranscriptPayload
): Promise<ApiResponse<Meeting>> => {
  try {
    const response = await api.post<ApiResponse<Meeting>>(
      MEETING_URLS.IMPORT_MOCK_TRANSCRIPT(meetingId),
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};
